import React, { useState, useEffect, ChangeEvent } from "react";
import { useToastStore } from "store/toastStore";
import { useGlobalLoader } from "contexts/GlobalLoaderContext";
import { getFeedbackTypes, submitFeedback, FeedbackType } from "apis/settingsServices";
import Select from "react-select";

const ContactUs: React.FC = () => {
  const { showToast } = useToastStore();
  const { showLoader, hideLoader } = useGlobalLoader();
  const [feedbackType, setFeedbackType] = useState<number | string>("");
  const [feedbackTypes, setFeedbackTypes] = useState<FeedbackType[]>([]);
  const [description, setDescription] = useState<string>("");
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [errors, setErrors] = useState<{ feedbackType?: string; description?: string }>({});
  const [loadingTypes, setLoadingTypes] = useState<boolean>(true);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [descriptionLengthError, setDescriptionLengthError] = useState<string>("");
  interface FeedbackOption {
    value: number;
    label: string;
  }

  // Fetch feedback types on component mount
  useEffect(() => {
    const fetchFeedbackTypes = async () => {
      try {
        setLoadingTypes(true);
        const types = await getFeedbackTypes();
        setFeedbackTypes(types);
      } catch (error) {
        console.error("Error fetching feedback types:", error);
        showToast("Failed to load feedback types", "error");
      } finally {
        setLoadingTypes(false);
      }
    };

    fetchFeedbackTypes();
  }, [showToast]);
  const feedbackOptions: FeedbackOption[] = feedbackTypes.map((type) => ({
    value: type.id,
    label: type.typeName,
  }));

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type (only PNG and JPG — server rejects JPEG)
      const fileName = file.name.toLowerCase();
      const isPng = file.type === "image/png" && fileName.endsWith(".png");
      const isJpg = (file.type === "image/jpeg" || file.type === "image/jpg") && fileName.endsWith(".jpg");
      if (!isPng && !isJpg) {
        showToast("Please select a valid image file (PNG or JPG only — JPEG not supported)", "error");
        return;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        showToast("Image size should be less than 5MB", "error");
        return;
      }

      setSelectedImage(file);

      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveImage = () => {
    setSelectedImage(null);
    setImagePreview(null);
  };

  const validateForm = (): boolean => {
    const newErrors: { feedbackType?: string; description?: string } = {};

    if (!feedbackType) {
      newErrors.feedbackType = "Please select a feedback type";
    }

    if (!description.trim()) {
      newErrors.description = "Please provide a description";
    } else if (description.length > 255) {
      newErrors.description = "Description must be 255 characters or less";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0 && !descriptionLengthError;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      showLoader();
      setIsSubmitting(true);

      // Create FormData for multipart/form-data request
      const formData = new FormData();
      formData.append("FeedbackComment", description);
      formData.append("FeedbackTypeId", String(feedbackType));
      if (selectedImage) {
        formData.append("Image", selectedImage);
      }

      // Submit feedback via API
      await submitFeedback(formData);

      showToast("Feedback submitted successfully", "success");

      // Clear form
      setFeedbackType("");
      setDescription("");
      setSelectedImage(null);
      setImagePreview(null);
      setErrors({});
      setDescriptionLengthError("");
    } catch (error: any) {
      console.error("Error submitting feedback:", error);
      const backendMessage =
        error?.response?.data?.message ||
        error?.response?.data?.detail ||
        error?.message;
      showToast(backendMessage || "Failed to submit feedback. Please try again.", "error");
    } finally {
      hideLoader();
      setIsSubmitting(false);
    }
  };

   const isFormValid = feedbackType && description.trim().length > 0 && !descriptionLengthError;

  return (
    <div className="pb-0">
      <div className="bg-white rounded-xl p-6">
        {/* Section Header */}
        <div className="flex items-center gap-2 mb-6 pb-4 border-b border-gray-100">
          <i className="st-chat text-grease-100 text-lg"></i>
          <p className="text-grease-100 text-base font-semibold">Contact Us</p>
        </div>

        <form onSubmit={handleSubmit} className="max-w-lg space-y-5">
          {/* Feedback Type Dropdown */}
          <div>
            <label htmlFor="feedbackType" className="block text-xs font-medium text-grease-100 mb-1.5">
              Select a reason
            </label>
            <Select
              options={feedbackOptions}
              value={feedbackOptions.find((option) => option.value === feedbackType) || null}
              onChange={(selectedOption) => {
                const value = (selectedOption as FeedbackOption | null)?.value ?? "";
                setFeedbackType(value);
                if (errors.feedbackType) {
                  setErrors({ ...errors, feedbackType: undefined });
                }
              }}
              placeholder="Please select"
              isClearable
              isMulti={false}
              className="basic-single-select"
              classNamePrefix="select"
              styles={{
                control: (provided, state) => ({
                  ...provided,
                  backgroundColor: "#ffffff",
                  border: state.isFocused ? "1px solid #FFBC00" : "1px solid #e5e7eb",
                  boxShadow: state.isFocused ? "0 0 0 1px #FFBC00" : "none",
                  borderRadius: "8px",
                  minHeight: "44px",
                  padding: "0 4px",
                  transition: "all 0.2s ease-in-out",
                  "&:hover": {
                    borderColor: "#FFBC00",
                  },
                }),
                menu: (provided) => ({
                  ...provided,
                  backgroundColor: "#ffffff",
                  borderRadius: "8px",
                  marginTop: "4px",
                  boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.1)",
                }),
                option: (provided, state) => ({
                  ...provided,
                  backgroundColor: state.isSelected ? "#FEF3C7" : state.isFocused ? "#FFFBEB" : "#ffffff",
                  color: "#262626",
                  fontSize: "14px",
                  padding: "10px 14px",
                  cursor: "pointer",
                  ":active": {
                    backgroundColor: "#FEF3C7",
                  },
                }),
                placeholder: (provided) => ({
                  ...provided,
                  fontSize: "14px",
                  color: "#9CA3AF",
                }),
              }}
            />
            {errors.feedbackType && <p className="mt-1 text-sm text-red-500">{errors.feedbackType}</p>}
          </div>

          {/* Description Textarea */}
          <div>
            <label htmlFor="description" className="block text-xs font-medium text-grease-100 mb-1.5">
              Description
            </label>
            <textarea
              id="description"
              value={description}
              onChange={(e) => {
                const value = e.target.value;
                if (value.length > 255) {
                  setDescriptionLengthError("Description must be 255 characters or less");
                  return;
                }
                setDescription(value);
                setDescriptionLengthError("");
                if (errors.description) {
                  setErrors({ ...errors, description: undefined });
                }
              }}
              onKeyDown={(e) => {
                const target = e.target as HTMLTextAreaElement;
                if (target.value.length >= 255 && !['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab', 'Home', 'End'].includes(e.key) && !e.ctrlKey && !e.metaKey && !e.altKey) {
                  e.preventDefault();
                  setDescriptionLengthError("Description must be 255 characters or less");
                } else if (target.value.length < 255 && descriptionLengthError) {
                  setDescriptionLengthError("");
                }
              }}
              onPaste={(e) => {
                e.preventDefault();
                const pastedText = e.clipboardData.getData('text');
                const newValue = (description + pastedText).substring(0, 255);
                setDescription(newValue);
                if (newValue.length >= 255 && (description + pastedText).length > 255) {
                  setDescriptionLengthError("Description must be 255 characters or less");
                } else {
                  setDescriptionLengthError("");
                }
                if (errors.description) {
                  setErrors({ ...errors, description: undefined });
                }
              }}
              rows={5}
              placeholder="Describe your issue or feedback ."
              maxLength={255}
              className={`w-full p-3.5 rounded-lg text-sm text-grease-100 placeholder-gray-400 focus:outline-none resize-none border ${
                errors.description || descriptionLengthError
                  ? "border-red-500"
                  : "border-gray-200 focus:border-light-yellow focus:ring-1 focus:ring-light-yellow"
              }`}
            />
            <div className="flex justify-between items-center mt-1">
              {errors.description && <p className="text-xs text-red-500">{errors.description}</p>}
              {descriptionLengthError && <p className="text-xs text-red-500">{descriptionLengthError}</p>}
              <p className={`text-xs ml-auto ${description.length >= 255 ? "text-red-500" : "text-gray-400"}`}>
                {description.length}/255
              </p>
            </div>
          </div>

          {/* Image Upload */}
          <div>
            <label className="block text-xs font-medium text-grease-100 mb-1.5">Attach Screenshot (Optional)</label>

            {!imagePreview ? (
              <div>
                <input
                  type="file"
                  id="imageUpload"
                  accept=".png,.jpg"
                  onChange={handleImageChange}
                  className="hidden"
                />
                <label
                  htmlFor="imageUpload"
                  className="flex items-center justify-center w-full py-4 border-2 border-dashed border-light-yellow rounded-lg cursor-pointer hover:bg-yellow-50 transition-colors"
                >
                  <span className="text-sm text-grease-100 font-medium">Upload Image</span>
                </label>
              </div>
            ) : (
              <div className="relative inline-block">
                <img
                  src={imagePreview}
                  alt="Preview"
                  className="max-w-full h-auto max-h-48 rounded-lg border border-gray-200"
                />
                <button
                  type="button"
                  onClick={handleRemoveImage}
                  className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            )}
          </div>

          {/* Submit Button */}
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={!isFormValid || isSubmitting}
              className={`px-8 py-2.5 rounded-lg text-sm font-medium text-white bg-[#FFBC00] transition-opacity ${
                isFormValid && !isSubmitting
                  ? "hover:opacity-90 cursor-pointer"
                  : "opacity-50 cursor-not-allowed"
              }`}
            >
              {isSubmitting ? "Sending..." : "Save"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ContactUs;
